import React from 'react';
import { cn } from '../../lib/cn';

import type { CheckboxProps } from '../Checkbox/Checkbox';

export interface SwitchControlProps {
  checked?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  onChange?: (checked: boolean) => void;
  className?: string;
  onClick?: React.MouseEventHandler;
  ariaLabel?: string;
}

/** Theme-aware switch track — uses --ai-3 (on) and --surface-mute + --cream-2 (off). */
export function SwitchControl({
  checked = false,
  disabled,
  size = 'md',
  onChange,
  className,
  onClick,
  ariaLabel,
}: SwitchControlProps) {
  const sm = size === 'sm';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        onClick?.(e);
        if (!disabled) onChange?.(!checked);
      }}
      className={cn(
        'inline-flex shrink-0 cursor-pointer rounded-full border p-[3px] transition-[background,border-color] duration-[var(--dur-fast)]',
        sm ? 'h-[22px] w-[38px]' : 'h-[26px] w-11',
        checked
          ? 'justify-end border-[var(--ai-3)] bg-[var(--ai-3)]'
          : 'justify-start border-[var(--cream-2)] bg-[var(--surface-mute)]',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span
        className={cn(
          'rounded-full bg-[var(--cream-1)] shadow-[0_1px_3px_rgba(0,0,0,0.22)]',
          sm ? 'h-4 w-4' : 'h-5 w-5',
        )}
      />
    </button>
  );
}

export function Toggle({ label, checked, disabled, description, onChange }: CheckboxProps) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer items-center text-sm text-[var(--ink-1)]',
        label ? 'gap-3' : '',
        disabled && 'opacity-40',
      )}
    >
      <SwitchControl
        checked={checked ?? false}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(onChange ? { onChange } : {})}
      />
      {label ? (
        <span>
          {label}
          {description ? (
            <span className="block text-xs text-[var(--ink-3)]">{description}</span>
          ) : null}
        </span>
      ) : null}
    </label>
  );
}

export const Switch = Toggle;
export type ToggleProps = React.ComponentProps<typeof Toggle>;
